import { Button } from '@mm/components';
import { useViewActions } from '@mm/state/actions/viewActions';
import { isBilling2023V2Subscription } from '@mm/state/model/SubscriptionModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectActiveFacebookPage } from '@mm/state/selectors/facebookPageSelector';
import { Divider } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useImportContactsPermission } from '../../../../../common/hooks/use-import-contacts-permission';
import styles from './Contacts.module.css';
import { EnrichButton } from './EnrichButton/EnrichButton';
import { MyLeadsContactsTable } from './MyLeadsContactsTable/MyLeadsContactsTable';
import { MyLeadsFilterBar } from './MyLeadsFilterBar/MyLeadsFilterBar';
import { MyLeadsFiltersPanel } from './MyLeadsFiltersPanel/MyLeadsFiltersPanel';
import { useFilterParams } from './MyLeadsFiltersPanel/use-filter-params';
import { useFilterMyLeads } from './hooks/use-filter-my-leads';
import { useDummyContacts } from './hooks/use-dummy-contacts';
import { selectContacts } from '@mm/state/selectors/contactsSelector';
import { useCreateFilterParams } from './use-create-filter-params';
import { ContactsByChannelsStatistic } from '../Audiences/ContactsByChannelsStatistic/ContactsByChannelsStatistic';
import { MyLeadsTour, MyLeadsTourRefs } from './MyLeadsTour/MyLeadsTour';
import { Help } from '../Integrations/NativeIntegrations/components/KlaviyoIntegrations/KlaviyoHelpLink';

export const Contacts = () => {
  const { filterParams, setFilterParams } = useCreateFilterParams();

  const [isFetching, setIsFetching] = useState(false);

  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    setFilterParams({
      ...filterParams,
      page: 1,
    });
  };

  const { busy, canUseSorting } = useFilterMyLeads({
    fetchAttributes: true,
    filterParams,
    sortOrder,
  });

  const { openImportContactListModal, openAccountLockedModal } =
    useModalActions();
  const { showSlideoutMenu } = useViewActions();
  const uploadContactsPermission = useImportContactsPermission();
  const onImportClick = () => openImportContactListModal();
  const facebookPage = useSelector(selectActiveFacebookPage);
  const dummyLeads = useDummyContacts();
  const { allContactsCount } = useSelector(selectContacts);
  const [totalContacts, setTotalContacts] = useState(dummyLeads.length);
  const { hasFilters } = useFilterParams({ filterParams });
  const tourRefs = useRef<MyLeadsTourRefs>(null);

  useEffect(() => {
    setTotalContacts(allContactsCount || 0);
  }, [allContactsCount]);

  const processClick = (onSuccess: (args?: any) => void) => {
    if (
      facebookPage?.subscription?.failedPayment &&
      isBilling2023V2Subscription(facebookPage?.subscription)
    ) {
      openAccountLockedModal();
    } else {
      onSuccess();
    }
  };

  return (
    <div className={styles.container}>
      <MyLeadsTour ref={tourRefs} />
      <div className={styles.headerContainer}>
        All My Leads — {totalContacts?.toLocaleString('en-US')}
        <div className={styles.buttonsContainer}>
          <Help
            link="https://customers.ai/help/l/en/article/atu9epbs2x-attribute-and-tags"
            text="Help Center"
          />
          <EnrichButton />
          <Button
            css={{ container: styles.button }}
            disabled={!uploadContactsPermission.isAllowed}
            color="secondary"
            onClick={() => processClick(onImportClick)}
          >
            Import
          </Button>
          <Button
            color="secondary"
            onClick={() => showSlideoutMenu('attributesAndTags')}
            css={{ container: styles.button }}
          >
            Attributes and Tags
          </Button>
        </div>
      </div>

      <div className={styles.content}>
        <MyLeadsFiltersPanel
          title="Filters"
          filterParams={filterParams}
          setFilterParams={setFilterParams}
          busy={busy}
          ref={tourRefs.current?.filterPanelRef}
        />
        <div className={styles.contacts}>
          {!hasFilters && (
            <ContactsByChannelsStatistic
              busy={isFetching}
              withAudiences
              setIsFetching={setIsFetching}
            />
          )}
          <MyLeadsFilterBar
            filterParams={filterParams}
            setFilterParams={setFilterParams}
          />

          {hasFilters && <Divider style={{ margin: '12px 0px 22px' }} />}

          <MyLeadsContactsTable
            filterParams={filterParams}
            setFilterParams={setFilterParams}
            isFetchingContacts={busy}
            processClick={processClick}
            setTotalContacts={setTotalContacts}
            saveAudienceButtonRef={tourRefs.current?.saveAudienceButtonRef}
            leadRef={tourRefs.current?.leadRef}
            toggleSortOrder={toggleSortOrder}
            canUseSorting={canUseSorting}
            sortOrder={sortOrder}
          />
        </div>
      </div>
    </div>
  );
};
